import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Heading,
  Select,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {
  SingleFormField,
  SingleFormFieldProps,
} from "@ui/application/forms/src/common/single-field";
import { camelCase } from "change-case";
import { FormEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ThankYouModal } from "./thank-you-modal";

export interface EnterpriseContactDropdownsProps {
  label: string;
  enterpriseSelectFields: {
    option: string;
  }[];
}

export interface EnterpriseContactFormProps {
  title: string;
  subtitle: string;
  titleColor: string;
  labelColor: string;
  inputFields: SingleFormFieldProps[];
  enterpriseContactDropdowns: EnterpriseContactDropdownsProps[];
  button: string;
}

export const EnterpriseContactForm = (props: EnterpriseContactFormProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [inputValues, setInputValues] = useState<any>({
    name: "",
    email: "",
    jobTitle: "",
    organizationName: "",
    organizationSize: "",
    industry: "",
    details: "",
  });
  const {
    register,
    formState: { isSubmitting },
  } = useForm();

  useEffect(() => {
    const isValid = Object.keys(inputValues).every(
      (key) => key === "details" || inputValues[key],
    );
    setIsValidForm(isValid);
  }, [inputValues]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await fetch("/api/enterprise-contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...inputValues }),
    })
      .then((res) => {
        res.json();
        setIsOpen(true);
        setInputValues({
          name: "",
          email: "",
          jobTitle: "",
          organizationName: "",
          organizationSize: "",
          industry: "",
          details: "",
        });
      })
      .catch((error) => console.error(error));
  }

  return (
    <Box
      pl={{
        base: 8,
        lg: 12,
      }}
      pr={{
        base: 8,
        lg: 28,
      }}
      py={{ base: 10, md: 14, lg: 20 }}
      w={{
        lg: "50%",
      }}
    >
      <Heading as={"h2"} color={props.titleColor} pb={5} fontWeight={"bold"}>
        {props.title}
      </Heading>
      <Text pb={10}>{props.subtitle}</Text>
      <Flex flexDir={"column"}>
        {props.inputFields.slice(0, 4).map((field, idx) => (
          <FormControl key={idx} isRequired>
            <SingleFormField
              {...field}
              register={register(field.htmlFor)}
              inputSx={{
                border: "none",
                borderBottom: `2px solid ${props.labelColor}`,
                borderRadius: "none",
                focusBorderColor: "none",
                marginBottom: "10px",
                _placeholder: {
                  color: props.labelColor,
                },
              }}
              labelSx={{
                display: "none",
              }}
              formFor="enterprise"
              inputValues={inputValues}
              setInputValues={setInputValues}
            />
          </FormControl>
        ))}
        {props?.enterpriseContactDropdowns?.map((dropdown, idx) => (
          <Select
            key={idx}
            placeholder={`${dropdown.label} *`}
            id={camelCase(dropdown.label)}
            border={"none"}
            borderBottom={`2px solid ${props.labelColor}`}
            borderRadius="none"
            focusBorderColor="none"
            marginBottom="10px"
            color={props.labelColor}
            onChange={(e) =>
              setInputValues((prevState: any) => ({
                ...prevState,
                [camelCase(dropdown.label)]: e.target.value,
              }))
            }
            value={inputValues[`${camelCase(dropdown.label)}`]}
          >
            {dropdown.enterpriseSelectFields.map((item, idx) => (
              <option key={idx} value={item.option}>
                {item.option}
              </option>
            ))}
          </Select>
        ))}
        <Textarea
          id="details"
          h="80px"
          border={"none"}
          focusBorderColor="none"
          maxLength={2000}
          mb={5}
          placeholder={props.inputFields[4].placeholder}
          _placeholder={{
            color: props.labelColor,
          }}
          resize={"none"}
          overflowY={"auto"}
          onChange={(e) =>
            setInputValues((prevState: any) => ({
              ...prevState,
              details: e.target.value,
            }))
          }
          value={inputValues["details"]}
        />
      </Flex>
      <Flex pt={16} flexDir={"column"} gap={4}>
        <Divider
          orientation="horizontal"
          borderColor={props.labelColor}
          borderWidth={"1.3px"}
          opacity={100}
        />
        <Button
          type="submit"
          isLoading={isSubmitting}
          borderRadius={"full"}
          color={"white"}
          fontSize={14}
          w="130px"
          alignSelf={"end"}
          onClick={(e) => handleSubmit(e)}
          bgColor={isValidForm ? props.labelColor : "gray.400"}
          disabled={!isValidForm}
        >
          <Text textTransform={"uppercase"}>{props.button}</Text>
        </Button>
      </Flex>
      <ThankYouModal isOpen={isOpen} />
    </Box>
  );
};
